import { Divider, Grid, Typography, useEventCallback } from '@mui/material';
import { DocumentCivilStatusCivilUnionCrumbs } from 'civil-status/constants/breadcrumb';
import { UploadForm } from 'civil-status/view-models/file-extended';
import { useFormik } from 'formik';
import { PmiButton } from 'shell/components/shared/button';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { Cropper } from 'shell/components/shared/cropper';
import { PmiInputFile } from 'shell/components/shared/file-input';
import { StickyDiv } from 'shell/components/shared/sticky-div';
import { __CIVIL_STATUS_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { DocumentCivilUnionList } from 'shell/models/document-civil-union';
import { HttpResponse } from 'shell/models/http-response';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { ConvertedImage } from 'shell/models/convert-image';

export default function DocumentCivilUnionUpload(): React.ReactElement {
  const navigate = useNavigate();
  const params = useParams();
  const { userProfile } = useUserProfile();

  const [civilUnion] = useHttpFetch<HttpResponse<DocumentCivilUnionList>, DocumentCivilUnionList>(
    `${__CIVIL_STATUS_API_URL__}/atti-unione-civile-sima/${params.anno}-${params.numeroRegistro}-${
      params.numeroAtto
    }-${params.parte}-${params.serie1 === ' ' ? 'null' : params.serie1}`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
      },
      transformer: (response) => {
        const item = response.data?.payload;
        return item;
      },
    }
  );

  const unlockDocument = useHttpClient(
    `${__CIVIL_STATUS_API_URL__}/atti-unione-civile-sima/sblocca-atto`,
    {
      options: {
        errorCatching: true,
        method: 'PUT',
        loading: true,
      },
    }
  );

  const data = civilUnion.data as DocumentCivilUnionList;

  useEffect(() => {
    return () => {
      if (data?.key) {
        unlockDocument({
          data: {
            key: data?.key,
          },
        });
      }
    };
  }, [data]);

  const [insertMode, setInsertMode] = useState(false);

  const uploadDocument = useHttpClient(
    `${__CIVIL_STATUS_API_URL__}/atti-unione-civile-sima/caricamento-atto-unione-civile`,
    {
      options: {
        errorCatching: true,
        method: 'PUT',
        loading: true,
      },
    }
  );

  const handleUpload = useCallback(
    (values: UploadForm) => {
      (
        uploadDocument({
          data: {
            attoUnioneCivileForUploadVO: {
              numeroRegistro: data?.numeroRegistro,
              cognome1: data?.cognomeConiuge1,
              cognome2: data?.cognomeConiuge2,
              dichiarante: data?.dichiarante,
              filename: values.file?.name,
              matricola1: data?.matricolaConiuge1,
              matricola2: data?.matricolaConiuge2,
              nome1: data?.nomeConiuge1,
              nome2: data?.nomeConiuge2,
              anno: data?.anno,
              numeroAtto: data?.numeroAtto,
              parte: data?.parte,
              serie: `${data?.serie1 || ' '}${data?.serie2 || ''}`,
              idOperatore: userProfile.datiLogin?.key?.userName,
            },
            filename: values.file?.name,
            mimeType: values.file?.type,
            file: values?.rawTiff?.split(',')[1],
          },
        }) as ResponsePromise<void>
      ).then(() => {
        toast.success('Operazione eseguita con successo');
        navigate('/stato-civile/unione-civile');
      });
    },
    [uploadDocument, data, navigate]
  );

  const formContext = useFormik<UploadForm>({
    initialValues: {
      file: undefined,
      raw: [],
      rawTiff: '',
    },
    onSubmit: handleUpload,
  });

  const convertDocument = useHttpClient<HttpResponse<ConvertedImage>>(
    `${__CIVIL_STATUS_API_URL__}/convert-image`,
    {
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
      },
    }
  );

  const handleAddFile = useEventCallback((file: File) => {
    const reader = new FileReader();
    reader.addEventListener('load', (event) => {
      const { result } = event.target || {};
      (
        convertDocument({
          data: {
            formato: 'png',
            immagineIniziale: (result as string)?.split(',')[1],
          },
        }) as ResponsePromise<HttpResponse<ConvertedImage>>
      ).then((response) => {
        formContext.setValues({
          file,
          raw: response?.data?.payload?.immagineConvertita
            ? (response?.data?.payload?.immagineConvertita as Array<string>)?.map(
                (immagine: string) => `data:image/png;base64,${immagine}`
              )
            : [],
          rawTiff: result as string,
        });
      });
    });

    reader.readAsDataURL(file);
  });

  return (
    <>
      <Breadcrumbs items={DocumentCivilStatusCivilUnionCrumbs} base="stato-civile" />
      <Typography
        color="secondary.main"
        style={{
          fontSize: '26px',
        }}
      >
        Caricamento Atto
      </Typography>
      <form onSubmit={formContext.handleSubmit}>
        <Grid container spacing={2}>
          {insertMode ? (
            <Grid item xs={12} md={4}>
              Carica documento&nbsp;
              <PmiInputFile
                accept="image/tiff"
                multiple={false}
                file={formContext.values.file}
                showFilename
                onChange={handleAddFile}
                color="cdmBrandThird"
                tipMaxSize={300}
              />
              <div className="pmi-d-flex pmi-justify-content-end pmi-pt-4">
                <div>
                  <PmiButton
                    variant="outlined"
                    color="secondary"
                    onClick={() => setInsertMode(false)}
                  >
                    Indietro
                  </PmiButton>
                </div>
                <div className="pmi-pl-3">
                  <PmiButton variant="contained" color="primary" type="submit">
                    Carica
                  </PmiButton>
                </div>
              </div>
            </Grid>
          ) : (
            <Grid item xs={12} md={4}>
              <span>Anno: {data?.anno || ''}</span>
              <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                Registro: {data?.numeroRegistro || ''}
              </span>
              <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                Atto: {data?.numeroAtto || ''}
              </span>
              <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                Parte: {data?.parte || ''}
              </span>
              <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                Serie: {data?.serie1 || ''}
                {data?.serie2 || ''}
              </span>
              <Typography
                color="cdmBrandThird.main"
                style={{
                  fontSize: '26px',
                }}
              >
                Atto di unione civile
              </Typography>
              <div className="pmi-pt-3">
                <span>Data di Unione Civile</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.dataMatrimonio
                    ? DateTime.fromISO(data.dataMatrimonio).toFormat('dd/MM/yyyy')
                    : ''}
                </span>
              </div>
              <div className="pmi-pt-3">
                <span>Comune di Unione Civile</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.comuneMatrimonio}
                </span>
              </div>
              <Divider />

              <div className="pmi-pt-3">
                <span>Matricola</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.matricolaConiuge1 || ''}
                </span>
              </div>
              <div className="pmi-pt-1">
                <span>Cognome</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.cognomeConiuge1 || ''}
                </span>
              </div>
              <div className="pmi-pt-1">
                <span>Nome</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.nomeConiuge1 || ''}
                </span>
              </div>
              <div className="pmi-pt-1">
                <span>Sesso</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.sessoConiuge1 || ''}
                </span>
              </div>
              <div className="pmi-pt-1">
                <span>Data di Nascita</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.dataNascitaConiuge1
                    ? DateTime.fromISO(data?.dataNascitaConiuge1).toFormat('dd/MM/yyyy')
                    : ''}
                </span>
              </div>
              <div className="pmi-pt-1 pmi-pl-2">
                <span>Anni</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.anniConiuge1 || ''}
                </span>
              </div>
              <div className="pmi-pt-1 pmi-pl-2">
                <span>Anno di Nascita</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.annoNascitaConiuge1 || ''}
                </span>
              </div>
              <div className="pmi-pt-1">
                <span>Comune di Nascita</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.comuneNascitaConiuge1 || ''}
                </span>
              </div>
              <div className="pmi-pt-1">
                <span>Comune di Residenza</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.comuneResidenzaConiuge1 || ''}
                </span>
              </div>
              <div className="pmi-pt-1">
                <span>Stato Civile</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.statoCivileConiuge1 || ''}
                </span>
              </div>
              <div className="pmi-pt-1">
                <span>Cognome Post</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.cognomeDopoMatrimonioConiuge1 || ''}
                </span>
              </div>
              <div className="pmi-pt-3">
                <Divider />
              </div>
              <div className="pmi-pt-3">
                <span>Matricola</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.matricolaConiuge2 || ''}
                </span>
              </div>
              <div className="pmi-pt-1">
                <span>Cognome</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.cognomeConiuge2 || ''}
                </span>
              </div>
              <div className="pmi-pt-1">
                <span>Nome</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.nomeConiuge2 || ''}
                </span>
              </div>
              <div className="pmi-pt-1">
                <span>Sesso</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.sessoConiuge2 || ''}
                </span>
              </div>
              <div className="pmi-pt-1">
                <span>Data di Nascita</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.dataNascitaConiuge2
                    ? DateTime.fromISO(data?.dataNascitaConiuge2).toFormat('dd/MM/yyyy')
                    : ''}
                </span>
              </div>
              <div className="pmi-pt-1 pmi-pl-2">
                <span>Anni</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.anniConiuge2 || ''}
                </span>
              </div>
              <div className="pmi-pt-1 pmi-pl-2">
                <span>Anno di Nascita</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.annoNascitaConiuge2 || ''}
                </span>
              </div>

              <div className="pmi-pt-1">
                <span>Comune di Nascita</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.comuneNascitaConiuge2 || ''}
                </span>
              </div>
              <div className="pmi-pt-1">
                <span>Comune di Residenza</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.comuneResidenzaConiuge2 || ''}
                </span>
              </div>
              <div className="pmi-pt-1">
                <span>Stato Civile</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.statoCivileConiuge2 || ''}
                </span>
              </div>
              <div className="pmi-pt-1">
                <span>Cognome Post</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.cognomeDopoMatrimonioConiuge2 || ''}
                </span>
              </div>
              <div className="pmi-d-flex pmi-justify-content-end pmi-pt-4">
                <div>
                  <PmiButton
                    variant="outlined"
                    color="secondary"
                    onClick={() => navigate('/stato-civile/unione-civile')}
                  >
                    Indietro
                  </PmiButton>
                </div>
                <div className="pmi-pl-3">
                  <PmiButton
                    variant="contained"
                    color="primary"
                    onClick={() => setInsertMode(true)}
                  >
                    Inserisci
                  </PmiButton>
                </div>
              </div>
            </Grid>
          )}

          <Grid item xs={12} md={8}>
            <StickyDiv>
              <Cropper
                images={formContext.values.raw || []}
                disabled={!formContext.values?.raw?.length}
              />
            </StickyDiv>
          </Grid>
        </Grid>
      </form>
    </>
  );
}

DocumentCivilUnionUpload.displayName = 'Document Civil union upload page component';
